import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  FaBan,
  FaCheck,
  FaChevronLeft,
  FaChevronRight,
  FaEdit,
  FaFilter,
  FaSearch,
  FaSortAmountDown,
  FaUser,
} from "react-icons/fa";
import {
  getAgentMerchants,
  getAgentMerchantsStats,
  getPortalMerchantSummary,
  getPortalMerchantsList,
  getMerchantApprovalQueue,
  getMerchantHealthDistribution,
  sendMerchantApprovalRequest,
} from "../api/api";
import { getOrgId } from "../utils/authStorage";
import { useToast } from "../context/ToastContext";
import "../styles/MerchantsPage.css";

const PAGE_SIZE = 10;

/* UI control -> API query value */
const SORT_MAP = {
  Newest: "newest",
  Oldest: "oldest",
  "Highest Volume": "volume",
  Health: "health",
};
const TAB_MAP = {
  all: "all",
  approval: "approval",
  tiers: "all",
};

const num = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const normalizeStatus = (status = "") => {
  const raw = String(status).toLowerCase();
  if (["inactive", "failed", "disabled"].includes(raw)) return "blocked";
  if (["pending", "updated", "review"].includes(raw)) return "pending";
  return "active";
};

const parseVolume = (value) => {
  if (typeof value === "number") return value;
  const numeric = Number(String(value ?? "").replace(/[^\d.]/g, ""));
  return Number.isFinite(numeric) ? numeric : 0;
};

const formatMoney = (value) => `Rs ${Number(value || 0).toLocaleString()}`;
const formatCompactMoney = (value) => {
  const n = Number(value || 0);
  if (n >= 1000000) return `Rs ${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `Rs ${(n / 1000).toFixed(1)}K`;
  return `Rs ${n.toLocaleString()}`;
};

const formatQr = (value) => {
  const n = Number(value || 0);
  if (n >= 1000) return `${(n / 1000).toFixed(1)}k`;
  return String(n);
};

const normalizeMerchant = (merchant, idx, agentName = "") => {
  const status = normalizeStatus(merchant.status);
  return {
    ...merchant,
    _id: merchant._id || merchant.merchantID || `m-${idx}`,
    merchantID: merchant.merchantID || merchant.MID || `MF-${90000 + idx}`,
    merchantName: merchant.merchantName || merchant.name || "Unknown Merchant",
    dbaName: merchant.dbaName || merchant.merchantName || "—",
    status,
    assignedAgent:
      merchant.assignedAgent ||
      merchant.agentName ||
      merchant.agent ||
      agentName ||
      "Unassigned",
    staticQr: Number(merchant.staticQr ?? merchant.staticQrCount ?? merchant.staticCount ?? 0),
    dynamicQr: Number(merchant.dynamicQr ?? merchant.dynamicQrCount ?? merchant.dynamicCount ?? 0),
    volume: parseVolume(merchant.volume ?? merchant.totalVolume ?? merchant.monthlyVolume),
    lastActivity: merchant.lastActivity || merchant.lastTxnAt || merchant.updatedAt || "—",
    lastNote: merchant.lastNote || "Verification pending",
    categoryName: merchant.categoryName || merchant.category || "General",
    region: merchant.region || merchant.country || "EMEA",
    healthScore: Number(merchant.healthScore ?? (status === "active" ? 86 : status === "pending" ? 47 : 16)),
  };
};

/* Pull the merchant array out of whatever envelope the API returns */
const extractList = (res) => {
  const payload = res?.data ?? res;
  return (
    (Array.isArray(payload) && payload) ||
    (Array.isArray(payload?.merchants) && payload.merchants) ||
    (Array.isArray(payload?.data) && payload.data) ||
    (Array.isArray(res?.merchants) && res.merchants) ||
    []
  );
};

const extractTotal = (res, fallback = 0) => {
  const payload = res?.data ?? res;
  return (
    num(
      res?.total ??
        res?.totalCount ??
        res?.count ??
        payload?.total ??
        payload?.totalCount ??
        payload?.count ??
        res?.pagination?.total ??
        payload?.pagination?.total ??
        res?.pagination?.totalRecords ??
        payload?.pagination?.totalRecords
    ) || fallback
  );
};

/* Normalize the summary KPI payload */
const extractSummary = (res) => {
  const d = res?.data ?? res ?? {};
  return {
    total: num(d.total ?? d.totalMerchants ?? d.merchants),
    active: num(d.active ?? d.activeMerchants),
    blocked: num(d.blocked ?? d.inactive ?? d.disabled ?? d.blockedMerchants),
    pending: num(d.pending ?? d.pendingMerchants ?? d.approval ?? d.pendingApprovals),
    volume: num(d.volume ?? d.totalVolume ?? d.transactionVolume ?? d.transVolume),
    avgTicket: num(d.avgTicket ?? d.averageTicket ?? d.avgTicketSize),
  };
};

/* Normalize the health distribution payload */
const extractHealth = (res) => {
  const d = res?.data ?? res ?? {};
  return {
    excellent: num(d.excellent ?? d.Excellent),
    good: num(d.good ?? d.Good),
    risk: num(d.risk ?? d.atRisk ?? d.at_risk ?? d.AtRisk),
    critical: num(d.critical ?? d.Critical),
  };
};

const AgentMerchantsDetails = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { showSuccess, showError } = useToast();
  const agentData = location.state?.user || {};
  const agentId = agentData?._id;
  const isAgentMode = Boolean(agentId);
  const orgId = useMemo(() => getOrgId(), []);

  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [category, setCategory] = useState("All");
  const [region, setRegion] = useState("All");
  const [sort, setSort] = useState("Newest");
  const [activeTab, setActiveTab] = useState("all");
  const [page, setPage] = useState(1);

  /* Agent-mode (client-side) data */
  const [stats, setStats] = useState(null);
  const [rawMerchants, setRawMerchants] = useState([]);

  /* Org-mode (server-driven) data */
  const [orgRows, setOrgRows] = useState([]);
  const [orgTotal, setOrgTotal] = useState(0);
  const [orgTotalPages, setOrgTotalPages] = useState(1);
  const [orgSummary, setOrgSummary] = useState(null);
  const [orgApproval, setOrgApproval] = useState([]);
  const [orgHealth, setOrgHealth] = useState({ excellent: 0, good: 0, risk: 0, critical: 0 });
  const [catOptions, setCatOptions] = useState(["All"]);
  const [regionOptions, setRegionOptions] = useState(["All"]);
  const [approvingId, setApprovingId] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  /* Debounce search box before hitting the API */
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 400);
    return () => clearTimeout(timer);
  }, [search]);

  /* Reset to first page whenever a filter changes */
  useEffect(() => {
    setPage(1);
  }, [activeTab, category, region, sort, debouncedSearch]);

  /* -------- Agent mode: fetch the agent's whole portfolio (client-side) -------- */
  useEffect(() => {
    if (!isAgentMode) return;
    let cancelled = false;
    const fetchData = async () => {
      setLoading(true);
      try {
        const [statsRes, merchantsRes] = await Promise.all([
          getAgentMerchantsStats(agentId),
          getAgentMerchants(agentId, new URLSearchParams({ page: 1, limit: 500 })),
        ]);
        if (cancelled) return;

        let merchantRows = [];
        let fetchedStats = null;
        if (statsRes?.success) fetchedStats = statsRes.data;
        if (merchantsRes?.success && Array.isArray(merchantsRes.data)) {
          merchantRows = merchantsRes.data;
        }

        const normalized = merchantRows.map((m, idx) =>
          normalizeMerchant(m, idx, agentData?.name)
        );
        setRawMerchants(normalized);

        if (fetchedStats) {
          setStats({
            total: Number(fetchedStats.total ?? normalized.length),
            active: Number(fetchedStats.active ?? normalized.filter((m) => m.status === "active").length),
            blocked: Number(
              fetchedStats.inactive ??
                fetchedStats.disabled ??
                normalized.filter((m) => m.status === "blocked").length
            ),
          });
        }
      } catch (error) {
        console.error("Failed loading agent merchants:", error);
        if (!cancelled) setRawMerchants([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchData();
    return () => {
      cancelled = true;
    };
  }, [agentData?.name, agentId, isAgentMode]);

  /* -------- Org mode: summary / approval queue / health (filter-independent) -------- */
  useEffect(() => {
    if (isAgentMode) return;
    let cancelled = false;
    const fetchAside = async () => {
      try {
        const [summaryRes, approvalRes, healthRes] = await Promise.all([
          getPortalMerchantSummary(orgId),
          getMerchantApprovalQueue(orgId, 5),
          getMerchantHealthDistribution(orgId),
        ]);
        if (cancelled) return;

        setOrgSummary(extractSummary(summaryRes));
        setOrgApproval(
          extractList(approvalRes).map((m, idx) => normalizeMerchant(m, idx))
        );
        setOrgHealth(extractHealth(healthRes));
      } catch (error) {
        console.error("Failed loading merchant summary widgets:", error);
      }
    };

    fetchAside();
    return () => {
      cancelled = true;
    };
  }, [isAgentMode, orgId, refreshKey]);

  /* -------- Org mode: paginated / filtered list (server-side) -------- */
  useEffect(() => {
    if (isAgentMode) return;
    let cancelled = false;
    const fetchList = async () => {
      setLoading(true);
      try {
        const res = await getPortalMerchantsList({
          orgId,
          page,
          limit: PAGE_SIZE,
          tab: TAB_MAP[activeTab] || "all",
          category: category === "All" ? "all" : category,
          region: region === "All" ? "all" : region,
          sort: SORT_MAP[sort] || "newest",
          search: debouncedSearch,
        });
        if (cancelled) return;

        const rows = extractList(res).map((m, idx) => normalizeMerchant(m, idx));
        const total = extractTotal(res, rows.length);

        setOrgRows(rows);
        setOrgTotal(total);
        setOrgTotalPages(Math.max(1, Math.ceil(total / PAGE_SIZE)));

        /* Keep filter dropdowns populated without collapsing as results narrow */
        setCatOptions((prev) => {
          const merged = new Set(prev);
          rows.forEach((m) => m.categoryName && merged.add(m.categoryName));
          return ["All", ...[...merged].filter((v) => v !== "All")];
        });
        setRegionOptions((prev) => {
          const merged = new Set(prev);
          rows.forEach((m) => m.region && merged.add(m.region));
          return ["All", ...[...merged].filter((v) => v !== "All")];
        });
      } catch (error) {
        console.error("Failed loading merchants:", error);
        if (!cancelled) {
          setOrgRows([]);
          setOrgTotal(0);
          setOrgTotalPages(1);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchList();
    return () => {
      cancelled = true;
    };
  }, [isAgentMode, orgId, page, activeTab, category, region, sort, debouncedSearch, refreshKey]);

  /* -------- Agent-mode derived view (client-side filtering/paging) -------- */
  const agentCategories = useMemo(
    () => ["All", ...new Set(rawMerchants.map((m) => m.categoryName).filter(Boolean))],
    [rawMerchants]
  );
  const agentRegions = useMemo(
    () => ["All", ...new Set(rawMerchants.map((m) => m.region).filter(Boolean))],
    [rawMerchants]
  );

  const agentFiltered = useMemo(() => {
    const q = debouncedSearch.trim().toLowerCase();
    let rows = rawMerchants.filter((m) => {
      const matchesSearch =
        !q ||
        m.merchantName.toLowerCase().includes(q) ||
        m.merchantID.toLowerCase().includes(q) ||
        String(m.assignedAgent).toLowerCase().includes(q);
      const matchesCategory = category === "All" || m.categoryName === category;
      const matchesRegion = region === "All" || m.region === region;
      const matchesTab =
        activeTab === "all" ||
        (activeTab === "approval" && m.status === "pending") ||
        (activeTab === "tiers" && m.healthScore >= 60);
      return matchesSearch && matchesCategory && matchesRegion && matchesTab;
    });

    rows = [...rows].sort((a, b) => {
      if (sort === "Highest Volume") return b.volume - a.volume;
      if (sort === "Health") return b.healthScore - a.healthScore;
      if (sort === "Oldest") return String(a.merchantID).localeCompare(String(b.merchantID));
      return String(b.merchantID).localeCompare(String(a.merchantID));
    });
    return rows;
  }, [activeTab, category, rawMerchants, region, debouncedSearch, sort]);

  const agentAggregate = useMemo(() => {
    const total = rawMerchants.length;
    const active = rawMerchants.filter((m) => m.status === "active").length;
    const blocked = rawMerchants.filter((m) => m.status === "blocked").length;
    const volume = rawMerchants.reduce((sum, m) => sum + m.volume, 0);
    const avgTicket = total ? Math.round(volume / total / 20) : 0;
    return {
      total,
      active,
      blocked,
      volume,
      avgTicket,
      excellent: rawMerchants.filter((m) => m.healthScore >= 80).length,
      good: rawMerchants.filter((m) => m.healthScore >= 60 && m.healthScore < 80).length,
      risk: rawMerchants.filter((m) => m.healthScore >= 35 && m.healthScore < 60).length,
      critical: rawMerchants.filter((m) => m.healthScore < 35).length,
    };
  }, [rawMerchants]);

  const agentApprovalQueue = useMemo(
    () => rawMerchants.filter((m) => m.status === "pending" || m.status === "blocked").slice(0, 3),
    [rawMerchants]
  );

  /* -------- Unified view model (agent vs org) -------- */
  const totalCount = isAgentMode ? agentFiltered.length : orgTotal;
  const totalPages = isAgentMode
    ? Math.max(1, Math.ceil(agentFiltered.length / PAGE_SIZE))
    : orgTotalPages;
  const currentPage = Math.min(page, totalPages);
  const tableRows = isAgentMode
    ? agentFiltered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE)
    : orgRows;

  const startIdx = totalCount === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1;
  const endIdx = isAgentMode
    ? Math.min(currentPage * PAGE_SIZE, totalCount)
    : (currentPage - 1) * PAGE_SIZE + tableRows.length;

  const categories = isAgentMode ? agentCategories : catOptions;
  const regions = isAgentMode ? agentRegions : regionOptions;

  const approvalList = isAgentMode ? agentApprovalQueue : orgApproval;
  const health = isAgentMode ? agentAggregate : orgHealth;

  const summaryView = isAgentMode
    ? {
        total: Number(stats?.total ?? agentAggregate.total),
        active: Number(stats?.active ?? agentAggregate.active),
        blocked: Number(stats?.blocked ?? agentAggregate.blocked),
        volume: agentAggregate.volume,
        avgTicket: agentAggregate.avgTicket,
      }
    : {
        total: Number(orgSummary?.total ?? orgTotal),
        active: Number(orgSummary?.active ?? 0),
        blocked: Number(orgSummary?.blocked ?? 0),
        volume: Number(orgSummary?.volume ?? 0),
        avgTicket: Number(orgSummary?.avgTicket ?? 0),
      };

  const headerStats = [
    { label: "Total Merchants", value: summaryView.total.toLocaleString(), trend: "0%", tone: "mint" },
    { label: "Active", value: summaryView.active.toLocaleString(), trend: "92.3% Retention", tone: "cyan" },
    { label: "Blocked", value: summaryView.blocked.toLocaleString(), trend: "0 since last week", tone: "rose" },
    { label: "Trans. Volume", value: formatCompactMoney(summaryView.volume), trend: "0% MoM", tone: "mint" },
    { label: "Avg Ticket", value: formatMoney(summaryView.avgTicket), trend: "Optimized range", tone: "amber" },
  ];

  /* Approve a merchant (org mode) */
  const handleApprove = async (merchant) => {
    const id = merchant.merchantID || merchant.MID || merchant._id;
    if (!id || approvingId) return;
    try {
      setApprovingId(id);
      await sendMerchantApprovalRequest(id);
      showSuccess("Approval requested", `${merchant.merchantName || "Merchant"} sent for approval`);
      setRefreshKey((k) => k + 1);
    } catch (error) {
      showError("Error", error?.response?.data?.message || "Failed to send approval request");
    } finally {
      setApprovingId(null);
    }
  };

  return (
    <div className="mp-page">
      <div className="mp-head">
        <div>
          <h1 className="mp-title">Merchants</h1>
          <p className="mp-sub">
            {isAgentMode ? `${agentData?.name || "Selected Agent"} portfolio` : "Organization merchant network overview"}
          </p>
        </div>

        <div className="mp-search">
          <FaSearch />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search merchants, IDs, or agents..."
          />
        </div>
      </div>

      <div className="mp-kpis">
        {headerStats.map((item) => (
          <div key={item.label} className="mp-kpi">
            <div className="mp-kpi__label">{item.label}</div>
            <div className="mp-kpi__value">{loading ? "—" : item.value}</div>
            <div className={`mp-kpi__trend mp-kpi__trend--${item.tone}`}>{item.trend}</div>
          </div>
        ))}
      </div>

      <div className="mp-toolbar">
        <div className="mp-tabs">
          <button className={activeTab === "all" ? "active" : ""} onClick={() => setActiveTab("all")}>All Merchants</button>
          <button className={activeTab === "approval" ? "active" : ""} onClick={() => setActiveTab("approval")}>
            Approval Queue ({approvalList.length})
          </button>
          <button className={activeTab === "tiers" ? "active" : ""} onClick={() => setActiveTab("tiers")}>Performance Tiers</button>
        </div>

        <div className="mp-controls">
          <label>
            <FaFilter />
            <span>Category:</span>
            <select value={category} onChange={(e) => setCategory(e.target.value)}>
              {categories.map((entry) => (
                <option key={entry} value={entry}>{entry}</option>
              ))}
            </select>
          </label>
          <label>
            <span>Region:</span>
            <select value={region} onChange={(e) => setRegion(e.target.value)}>
              {regions.map((entry) => (
                <option key={entry} value={entry}>{entry}</option>
              ))}
            </select>
          </label>
          <label>
            <FaSortAmountDown />
            <span>Sort:</span>
            <select value={sort} onChange={(e) => setSort(e.target.value)}>
              {["Newest", "Oldest", "Highest Volume", "Health"].map((entry) => (
                <option key={entry} value={entry}>{entry}</option>
              ))}
            </select>
          </label>
        </div>
      </div>

      <div className="mp-tablecard">
        <table className="mp-table">
          <thead>
            <tr>
              <th>Merchant</th>
              <th>Status &amp; Health</th>
              <th>Assigned Agent</th>
              <th>QR Usage</th>
              <th>Volume</th>
              <th>Last Activity</th>
              <th>Actions</th>
            </tr>
          </thead>

          <tbody>
            {loading ? (
              <tr>
                <td colSpan={7} className="mp-empty">Loading merchants...</td>
              </tr>
            ) : tableRows.length === 0 ? (
              <tr>
                <td colSpan={7} className="mp-empty">No merchants found.</td>
              </tr>
            ) : (
              tableRows.map((merchant) => (
                <tr key={merchant._id}>
                  <td>
                    <div className="mp-merchant">
                      <div className="mp-merchant__avatar">{merchant.merchantName.slice(0, 1)}</div>
                      <div>
                        <div className="mp-merchant__name">{merchant.merchantName}</div>
                        <div className="mp-merchant__id">ID: {merchant.merchantID}</div>
                      </div>
                    </div>
                  </td>

                  <td>
                    <div className={`mp-status mp-status--${merchant.status}`}>{merchant.status}</div>
                    <div className="mp-health">
                      <span>{merchant.healthScore}</span>
                    </div>
                  </td>

                  <td className="mp-agent">{merchant.assignedAgent}</td>

                  <td>
                    <div className="mp-qr">
                      <div>
                        <b>{formatQr(merchant.staticQr)}</b>
                        <small>static</small>
                      </div>
                      <div>
                        <b>{formatQr(merchant.dynamicQr)}</b>
                        <small>dynamic</small>
                      </div>
                    </div>
                  </td>

                  <td>
                    <div className="mp-volume">{formatMoney(merchant.volume)}</div>
                    <small>{merchant.dynamicQr + merchant.staticQr} txns</small>
                  </td>

                  <td>
                    <div className="mp-last">{merchant.lastActivity}</div>
                    <small>{merchant.lastNote}</small>
                  </td>

                  <td>
                    <div className="mp-actions">
                      <button
                        title="Open Profile"
                        onClick={() =>
                          navigate("/dashboard/merchantprofile", {
                            state: { merchant, name: merchant.merchantName },
                          })
                        }
                      >
                        <FaUser />
                      </button>
                      <button
                        title="Transactions"
                        onClick={() =>
                          navigate("/dashboard/merchant-transactions", {
                            state: { merchant, name: merchant.merchantName },
                          })
                        }
                      >
                        <FaEdit />
                      </button>
                      {!isAgentMode && merchant.status === "pending" ? (
                        <button
                          title="Approve Merchant"
                          disabled={approvingId === (merchant.merchantID || merchant._id)}
                          onClick={() => handleApprove(merchant)}
                        >
                          <FaCheck />
                        </button>
                      ) : (
                        <button title="Block / Suspend">
                          <FaBan />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        {!loading && (
          <div className="mp-tablefoot">
            <div>
              Showing {startIdx} to {endIdx} of {totalCount} merchants
            </div>

            <div className="mp-pager">
              <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={currentPage === 1}>
                <FaChevronLeft />
              </button>
              {Array.from({ length: Math.min(totalPages, 3) }, (_, idx) => idx + 1).map((n) => (
                <button key={n} className={n === currentPage ? "active" : ""} onClick={() => setPage(n)}>
                  {n}
                </button>
              ))}
              {totalPages > 3 && <span>...</span>}
              <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}>
                <FaChevronRight />
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="mp-bottom">
        <section className="mp-card">
          <div className="mp-card__head">
            <h3>Approval Queue</h3>
            <span>{approvalList.length} new</span>
          </div>

          <div className="mp-queue">
            {approvalList.length === 0 ? (
              <div className="mp-empty-small">No pending approvals.</div>
            ) : (
              approvalList.map((merchant) => (
                <button
                  key={merchant._id}
                  className="mp-queue__item"
                  type="button"
                  disabled={isAgentMode || approvingId === (merchant.merchantID || merchant._id)}
                  onClick={() => !isAgentMode && handleApprove(merchant)}
                >
                  <div>
                    <strong>{merchant.merchantName}</strong>
                    <small>{merchant.categoryName}</small>
                  </div>
                  <span>›</span>
                </button>
              ))
            )}
          </div>

          <button className="mp-card__link" type="button">View Full Queue</button>
        </section>

        <section className="mp-card mp-card--wide">
          <div className="mp-card__head">
            <h3>Merchant Health Distribution</h3>
            <span>Last 30 Days</span>
          </div>

          <div className="mp-healthbars">
            <div>
              <span style={{ height: `${Math.max(8, health.excellent * 14)}px` }} />
              <p>Excellent ({health.excellent})</p>
            </div>
            <div>
              <span style={{ height: `${Math.max(8, health.good * 14)}px` }} />
              <p>Good ({health.good})</p>
            </div>
            <div>
              <span style={{ height: `${Math.max(8, health.risk * 14)}px` }} />
              <p>At Risk ({health.risk})</p>
            </div>
            <div>
              <span style={{ height: `${Math.max(8, health.critical * 14)}px` }} />
              <p>Critical ({health.critical})</p>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default AgentMerchantsDetails;
